import React, { createContext, FC } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Api, espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'

import { useAuth } from 'hooks'
import { HandleCalculo, ICalculoContext } from './types'

type Props = {
    children: React.ReactNode
}

export const CalculoContext = createContext<ICalculoContext>(
    {} as ICalculoContext,
)

export const CalculoProvider: FC<Props> = ({ children }) => {
    const [searchParam] = useSearchParams()

    const navigate = useNavigate()
    const { mutateAsync } = Api.calculo.mutations.useCreate()

    const competencia = useStore(state => state.competencia)
    const competenciaEmpresaAtual = useStore(
        state => state.company?.competenciaAtual,
    )
    const funcionarioId = useStore(state => state.employeeId)

    const empresaId = useStore(state => state.companyId)
    const {
        decoded: { categoria },
    } = useAuth()

    const { data } = espelhoPonto.useFetchById(funcionarioId, {
        queryParams: {
            competencia: searchParam.get('competence') ?? competencia,
            empresaId,
        },
        enabled: empresaId > 0 && funcionarioId > 0,
        retry: false,
        cacheTime: 0,
        onError: async ({ statusCode, message }) => {
            if (
                statusCode === 400 &&
                message === 'Espelho não encontrado' &&
                categoria !== 5
            ) {
                await handleCalculo({
                    view: 'mensal',
                })
            }
        },
    })

    const handleCalculo: HandleCalculo = async arg => {
        const {
            view,
            justificativa,
            isTrusted,
            competencia: competenciaCalcular = competencia,
        } = arg

        if (!competenciaEmpresaAtual || !competenciaCalcular) return
        if (competenciaEmpresaAtual !== competenciaCalcular) {
            if (isTrusted)
                return toast.warning(
                    'A competência deve estar aberta para calcular esse funcionário',
                ) as any
            else return
        }
        if (funcionarioId === -1) return

        await mutateAsync(
            {
                competencia,
                empresaId,
                justificativa,
                ...(view === 'mensal'
                    ? {
                          funcionarioIds: [funcionarioId],
                      }
                    : {}),
            },
            {
                onSuccess: async () => {
                    await espelhoPonto.invalidateQueries()

                    arg.callback?.()
                    if (arg.navigateTo) navigate(arg.navigateTo)
                },
                onError: err => {
                    const message =
                        err?.message ||
                        err?.descricao ||
                        'Ocorreu um erro durante o cálculo e nenhuma mensagem de erro foi retornada.'

                    toast.warning(message)
                },
            },
        )
    }

    const getHorarios = (zIndex: number) => {
        if (data) {
            const espelhoPonto = data.EspelhoPonto[zIndex]

            return {
                horasTrabalhadas: espelhoPonto?.horasTrabalhadas || '00:00',
                faltas: espelhoPonto?.faltas || '00:00',
                horasExtra: espelhoPonto?.horasExtra || '00:00',
                sobreaviso: espelhoPonto?.sobreaviso || '00:00',
            }
        }

        return {
            horasTrabalhadas: '00:00',
            faltas: '00:00',
            horasExtra: '00:00',
            sobreaviso: '00:00',
        }
    }

    return (
        <CalculoContext.Provider
            value={{
                getHorarios,
                handleCalculo,
                espelho: data,
            }}
        >
            {children}
        </CalculoContext.Provider>
    )
}
